"use client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";

import { SCRIPT_SRC, getDefaultSettings } from "@stadium/services/zendesk/constants";

const defaultObj = {};

export const ZendeskContext = React.createContext(defaultObj);

export const zendeskHideCounter = (() => {
  let count = 0;

  return {
    increase: (c = 1) => (count += c),
    decrease: (c = 1) => (count -= c),
    get val() {
      return count;
    },
  };
})();

export const ZendeskProvider = ({ children, zendeskKey, blacklistedRoutes = [] }) => {
  const zendeskScriptSrc = `${SCRIPT_SRC}?key=${zendeskKey}`;

  const [zendeskLoaded, setZendeskLoaded] = useState(false);

  const pathname = usePathname();

  const showZenDeskIcon = () => {
    zendeskHideCounter.decrease();
    if (zendeskHideCounter.val === 0) window.zE?.("messenger", "show");
  };

  const hideZenDeskIcon = () => {
    zendeskHideCounter.increase();
    window.zE?.("messenger", "hide");
  };

  useEffect(() => {
    if (blacklistedRoutes?.includes(pathname)) {
      hideZenDeskIcon();
    } else if (zendeskHideCounter > 0) {
      showZenDeskIcon();
    }
  }, [pathname, zendeskLoaded]);

  const toggleZenDeskIcon = useCallback(
    ({ show = true }) => {
      if (show) {
        return showZenDeskIcon();
      }
      return hideZenDeskIcon();
    },
    [showZenDeskIcon, hideZenDeskIcon],
  );

  const value = useMemo(
    () => ({
      zendeskLoaded,
      showZenDeskIcon,
      hideZenDeskIcon,
      toggleZenDeskIcon,
    }),
    [zendeskLoaded, showZenDeskIcon, hideZenDeskIcon, toggleZenDeskIcon],
  );
  return (
    <>
      <Script id="zendesk-settings" strategy="lazyOnload">
        {getDefaultSettings()}
      </Script>
      <Script
        id="ze-snippet"
        onLoad={() => {
          setZendeskLoaded(true);
        }}
        src={zendeskScriptSrc}
        strategy="lazyOnload"
      />
      <ZendeskContext.Provider value={value}>{children}</ZendeskContext.Provider>
    </>
  );
};
